/**
 * Fog of war (M3 spec §6.1, §6.2; plan R5). A system is unknown, rumoured or charted; knowledge only ever rises.
 * Charting a system rumours the far end of every relay touching it, whatever the relay's status ("relays reveal
 * their neighbours", D-TBD 18).
 */
import type { Galaxy } from '../../galaxy/types'
import type { Knowledge, ReachState } from '../types'
import type { KnowledgeConfig } from './config'
import { farEnd, galaxyIndex } from './galaxyIndex'

export type KnownLevel = 'rumoured' | 'charted'

/**
 * The new-game knowledge: every system in `chartedRegions` is charted; every system within `rumourHops` online
 * relay hops of a charted system, and every `rumouredAnchors` system, is rumoured; the rest are absent (unknown).
 * Recomputed on load, never saved, because chart novelty compares against it.
 */
export function initialKnowledge(galaxy: Galaxy, cfg: KnowledgeConfig): Record<string, KnownLevel> {
  const out: Record<string, KnownLevel> = {}
  const charted = new Set<string>(cfg.chartedRegions)
  const anchors = new Set<string>(cfg.rumouredAnchors)
  const adjacency = new Map<string, string[]>()
  for (const s of galaxy.systems) adjacency.set(s.id, [])
  for (const r of galaxy.relays) {
    if (!r.online) continue
    adjacency.get(r.a)?.push(r.b)
    adjacency.get(r.b)?.push(r.a)
  }
  let frontier: string[] = []
  for (const s of galaxy.systems) {
    if (charted.has(s.region)) {
      out[s.id] = 'charted'
      frontier.push(s.id)
    }
  }
  for (let hop = 0; hop < cfg.rumourHops && frontier.length > 0; hop++) {
    const next: string[] = []
    for (const id of frontier) {
      for (const n of adjacency.get(id) ?? []) {
        if (out[n] !== undefined) continue
        out[n] = 'rumoured'
        next.push(n)
      }
    }
    frontier = next
  }
  for (const s of galaxy.systems) {
    if (s.anchor !== null && anchors.has(s.anchor) && out[s.id] === undefined) out[s.id] = 'rumoured'
  }
  return out
}

export function knowledgeOf(state: ReachState, systemId: string): Knowledge {
  return state.knowledge[systemId] ?? 'unknown'
}

/** Unknown → rumoured. True when it changed (a rumoured or charted system stays as it is). */
export function markRumoured(state: ReachState, systemId: string): boolean {
  if (state.knowledge[systemId] !== undefined) return false
  state.knowledge[systemId] = 'rumoured'
  return true
}

export interface ChartResult {
  /** True when the system itself became charted just now. */
  charted: boolean
  /** Systems that went from unknown to rumoured (the far ends of its relays). */
  rumoured: string[]
}

/**
 * Charts `systemId` (idempotent) and rumours the far end of every relay touching it, in any status. An id the
 * galaxy does not know changes nothing.
 */
export function markCharted(state: ReachState, galaxy: Galaxy, systemId: string): ChartResult {
  const system = galaxyIndex(galaxy).systems.get(systemId)
  if (!system) return { charted: false, rumoured: [] }
  const charted = state.knowledge[systemId] !== 'charted'
  state.knowledge[systemId] = 'charted'
  const rumoured: string[] = []
  const relays = galaxyIndex(galaxy).relays
  for (const relayId of system.relayIds) {
    const relay = relays.get(relayId)
    if (!relay) continue
    const other = farEnd(relay, systemId)
    if (markRumoured(state, other)) rumoured.push(other)
  }
  return { charted, rumoured }
}
